import React, { useEffect, useState, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getEmpresaById, updateEmpresa } from "../../../api/empresaApi";
import { getGrupos } from "../../../api/grupoApi";
import Formulario, { FormField } from "../../../components/Formulario";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { buscaFerramenta, Ferramenta } from "../../../types/empresas";
import {
  createGrupoFerramenta,
  getFerramentas,
} from "../../../api/ferramentasApi";
import { buscaGrupo } from "../../../types/grupo";

const EditarEmpresa: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [cnpjEmpresa, setCnpjEmpresa] = useState<string>("");
  const [nomeEmpresa, setNomeEmpresa] = useState<string>("");
  const [grupo, setGrupo] = useState<buscaGrupo | null>(null);
  const [grupos, setGrupos] = useState<buscaGrupo[]>([]);
  const [logoEmpresa, setLogoEmpresa] = useState<string>("");
  const [enderecoEmpresa, setEnderecoEmpresa] = useState<string>("");
  const [cepEmpresa, setCepEmpresa] = useState<string>("");
  const [bairroEmpresa, setBairroEmpresa] = useState<string>("");
  const [numeroEmpresa, setNumeroEmpresa] = useState<string>("");
  const [cidadeEmpresa, setCidadeEmpresa] = useState<string>("");
  const [ufEmpresa, setUfEmpresa] = useState<string>("");
  const [telefoneEmpresa, setTelefoneEmpresa] = useState<string>("");
  const [codigoDmsEmpresa, setCodigoDmsEmpresa] = useState<string>("");
  const [codigoDmsRevenda, setCodigoDmsRevenda] = useState<string>("");
  const [bandeiraEmpresa, setBandeiraEmpresa] = useState<string>("");
  const [ferramenta, setFerramenta] = useState<Ferramenta[]>([]);
  const [ferramentas, setFerramentas] = useState<buscaFerramenta[]>([]); 
  const [ferramentasSelecionadas, setFerramentasSelecionadas] = useState<Ferramenta[]>([]); 
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token não encontrado");
        }
        const empresaData = await getEmpresaById(token, id!);
        setCnpjEmpresa(empresaData.cnpj_empresa);
        setNomeEmpresa(empresaData.nome_empresa);
        setGrupo(empresaData.grupo);
        setLogoEmpresa(empresaData.logo_empresa);
        setEnderecoEmpresa(empresaData.endereco_empresa || "");
        setCepEmpresa(empresaData.cep_empresa || "");
        setBairroEmpresa(empresaData.bairro_empresa || "");
        setNumeroEmpresa(empresaData.numero_empresa || "");
        setCidadeEmpresa(empresaData.cidade_empresa || "");
        setUfEmpresa(empresaData.uf_empresa || "");
        setTelefoneEmpresa(empresaData.telefone_empresa || "");
        setCodigoDmsEmpresa(empresaData.codigo_dms_empresa || "");
        setCodigoDmsRevenda(empresaData.codigo_dms_revenda || "");
        setBandeiraEmpresa(empresaData.bandeira_empresa || "");
        setFerramenta(empresaData.ferramentas);
      } catch (error) {
        toast.error("Erro ao buscar Empresa!");
        console.error("Erro ao buscar Empresa:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchGrupos = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token não encontrado");
        }
        const gruposData = await getGrupos(token);
        setGrupos(gruposData);
      } catch (error) {
        toast.error("Erro ao buscar grupos!");
        console.error("Erro ao buscar grupos:", error);
      }
    };

    const fetchFerramentas = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token não encontrado");
        }
        const ferramentasData = await getFerramentas(token);
        setFerramentas(ferramentasData);
      } catch (error) {
        toast.error("Erro ao buscar ferramentas!");
        console.error("Erro ao buscar ferramentas:", error);
      }
    };

    fetchEmpresa();
    fetchGrupos();
    fetchFerramentas();
  }, [id]);

  useEffect(() => {
    if (ferramentas.length > 0 && ferramenta.length > 0) {
      const ferramentasDisponiveis = ferramentas.filter(
        (ferramentaDisponivel) =>
          !ferramenta.some(
            (ferramentaCadastrada) =>
              ferramentaCadastrada.id === ferramentaDisponivel.id
          )
      );
      setFerramentas(ferramentasDisponiveis);
    }
  }, [ferramentas, ferramenta]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token não encontrado");
      }

      await updateEmpresa(token, id!, {
        cnpj_empresa: cnpjEmpresa,
        nome_empresa: nomeEmpresa,
        id_grupo: grupo?.id || 0, 
        logo_empresa: logoEmpresa,
        endereco_empresa: enderecoEmpresa,
        cep_empresa: cepEmpresa,
        bairro_empresa: bairroEmpresa,
        numero_empresa: numeroEmpresa,
        cidade_empresa: cidadeEmpresa,
        uf_empresa: ufEmpresa,
        telefone_empresa: telefoneEmpresa,
        codigo_dms_empresa: codigoDmsEmpresa,
        codigo_dms_revenda: codigoDmsRevenda,
        bandeira_empresa: bandeiraEmpresa,
      });

      if (ferramentasSelecionadas.length > 0 && id !== undefined) {
        const numericId = Number(id);
        if (!isNaN(numericId)) {
          await Promise.all(
            ferramentasSelecionadas.map(async (ferramenta) => {
              await createGrupoFerramenta(
                token,
                numericId,
                ferramenta.id
              );
            })
          );
        } else {
          console.error("ID não pode ser convertido para número.");
        }
      }
      toast.success("Empresa atualizada com sucesso!");
      navigate("/empresas");
    } catch (error) {
      toast.error("Erro ao atualizar empresa!");
      console.error("Erro ao atualizar empresa:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleBack = () => {
    navigate("/empresas");
  };

  const handleGrupoChange = (event: React.SyntheticEvent, value: buscaGrupo | null) => {
    setGrupo(value);
  };

  const handleFerramentaChange = (
    event: React.SyntheticEvent,
    value: Ferramenta[]
  ) => {
    setFerramentasSelecionadas(value);
  };

  const fields: FormField[] = [
    {
      label: "CNPJ da Empresa",
      type: "text",
      value: cnpjEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setCnpjEmpresa(e.target.value),
    },
    {
      label: "Nome da Empresa",
      type: "text",
      value: nomeEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setNomeEmpresa(e.target.value),
    },
    {
      label: "Grupo",
      type: "custom",
      component: (
        <Autocomplete
          options={grupos}
          getOptionLabel={(option) => option.nome_grupo}
          value={grupo}
          onChange={handleGrupoChange}
          sx={{ marginBottom: 2 }}
          renderInput={(params) => (
            <TextField {...params} label="Grupo" variant="outlined" />
          )}
        />
      ),
    },
    {
      label: "Logo da Empresa",
      type: "text",
      value: logoEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setLogoEmpresa(e.target.value),
    },
    {
      label: "Endereço",
      type: "text",
      value: enderecoEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setEnderecoEmpresa(e.target.value),
    },
    {
      label: "CEP",
      type: "text",
      value: cepEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setCepEmpresa(e.target.value),
    },
    {
      label: "Bairro",
      type: "text",
      value: bairroEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setBairroEmpresa(e.target.value),
    },
    {
      label: "Número",
      type: "text",
      value: numeroEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setNumeroEmpresa(e.target.value),
    },
    {
      label: "Cidade",
      type: "text",
      value: cidadeEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setCidadeEmpresa(e.target.value),
    },
    {
      label: "UF",
      type: "text",
      value: ufEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setUfEmpresa(e.target.value),
    },
    {
      label: "Telefone",
      type: "text",
      value: telefoneEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setTelefoneEmpresa(e.target.value),
    },
    {
      label: "Código DMS Empresa",
      type: "text",
      value: codigoDmsEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setCodigoDmsEmpresa(e.target.value),
      subtitle:
        "Usado quando é uma empresa que usa o Integrador pra conectar via Banco de dados e é necessário o preenchimento do código da empresa cadastrado no banco do cliente.",
    },
    {
      label: "Código DMS Revenda",
      type: "text",
      value: codigoDmsRevenda,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setCodigoDmsRevenda(e.target.value),
      subtitle:
        "Usado quando é uma empresa que usa o Integrador ou usa a API da LINX e é necessário o preenchimento do código da revenda cadastrado no banco do cliente.",
    },
    {
      label: "Bandeira",
      type: "text",
      value: bandeiraEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setBandeiraEmpresa(e.target.value),
      subtitle:
        "Usado quando é a API LINX para poder realizar algumas requisições via API é necessário esse preenchimento. Exemplo: \"CHER\"",
    },
    {
      label: "Ferramentas",
      type: "autocomplete",
      value: ferramentasSelecionadas,
      required: false,
      onChangeAutocomplete: handleFerramentaChange,
      options: ferramentas,
      getOptionLabel: (option: Ferramenta) => option.nome_ferramenta, 
      multiple: true,
    },
  ];

  return (
    <>
      <Formulario
        title="Editar Empresa"
        fields={fields}
        onSubmit={handleSubmit}
        onBack={handleBack}
        loading={loading || updating}
      />
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Ferramentas Cadastradas:</Typography>
        {ferramenta.length === 0 ? (
          <Typography variant="body1" mt={2}>
            Nenhuma ferramenta cadastrada para essa empresa.
          </Typography>
        ) : (
          <TableContainer component={Box} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome da Ferramenta</TableCell>
                  <TableCell>Nome Token Ferramenta</TableCell>
                  <TableCell>Habilidade Ferramenta</TableCell>
                  <TableCell>Pivot ID Empresa</TableCell>
                  <TableCell>Pivot ID Ferramenta</TableCell>
                  <TableCell>Pivot Ativo Empresa Ferramenta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ferramenta.map((ferramenta) => (
                  <TableRow key={ferramenta.id}>
                    <TableCell>{ferramenta.id}</TableCell>
                    <TableCell>{ferramenta.nome_ferramenta}</TableCell>
                    <TableCell>{ferramenta.nome_token_ferramenta}</TableCell>
                    <TableCell>{ferramenta.habilidade_ferramenta}</TableCell>
                    <TableCell>{ferramenta.pivot.id_empresa}</TableCell>
                    <TableCell>{ferramenta.pivot.id_ferramenta}</TableCell>
                    <TableCell>
                      {ferramenta.pivot.ativo_empresa_ferramenta}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </>
  );
};

export default EditarEmpresa;